"use client";

import { useForm, SubmitHandler } from "react-hook-form";
import { signIn, signOut /*, signInWithRedirect*/ } from "aws-amplify/auth";
import { useRouter, useSearchParams } from "next/navigation";
import React, { Suspense, useState } from "react";
import { isAmplifyError } from "@performup/amplify-utils";
import * as Sentry from "@sentry/nextjs";

import Button from "@performup/ui/components/custom-button";

type FormValues = {
  email: string;
  password: string;
};

/*
 * The browser can hold a session the server rejects (so the user lands here)
 * while Amplify still considers it signed in and refuses a new signIn. The
 * user just entered their credentials, so drop that session and sign in again.
 */
const signInClearingStaleSession = async (
  username: string,
  password: string,
): ReturnType<typeof signIn> => {
  try {
    return await signIn({ username, password });
  } catch (error) {
    if (
      !isAmplifyError(error) ||
      error.name !== "UserAlreadyAuthenticatedException"
    ) {
      throw error;
    }
    await signOut();
    return await signIn({ username, password });
  }
};

function LoginForm() {
  const params = useSearchParams();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsLoading(true);
    setLoginError(null); // Reset error message before trying
    const redirectUrl = params?.get("redirect");

    try {
      const { isSignedIn, nextStep } = await signInClearingStaleSession(
        data.email,
        data.password,
      );

      if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        router.replace(
          `/email-verification?email=${encodeURIComponent(data.email)}`,
        );
        return;
      }

      if (isSignedIn) {
        router.replace(redirectUrl || "/home");
      } else {
        setLoginError("Invalid email or password.");
        setIsLoading(false);
      }
    } catch (error) {
      if (isAmplifyError(error)) {
        if (error.name === "NotAuthorizedException") {
          setLoginError("Incorrect email or password.");
        } else if (error.name === "UserNotFoundException") {
          setLoginError("User does not exist.");
        } else if (error.name === "UserNotConfirmedException") {
          setLoginError("You must confirm your email before logging in.");
        } else {
          setLoginError("An unexpected error occurred. Please try again.");
          console.error(error);
          Sentry.captureException(error, {
            tags: { source: "cognito", action: "signIn" },
          });
        }
      } else {
        setLoginError("An unexpected error occurred. Please try again.");
        console.error(error);
        Sentry.captureException(error, {
          tags: { source: "cognito", action: "signIn" },
        });
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="w-full max-w-lg mx-3 bg-white p-10 rounded-lg shadow-lg">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {loginError && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <span className="block sm:inline">{loginError}</span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                type="input"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                    message: "Invalid email address",
                  },
                })}
                className={`mt-2 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                {...register("password", {
                  required: "Password is required",
                })}
                className={`mt-2 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>
            <div className="flex items-center justify-center">
              <Button
                className="mt-2"
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                Log In
              </Button>
            </div>
          </form>
          {/*<div className="mt-6 flex items-center gap-4">*/}
          {/*  <div className="h-px flex-1 bg-gray-300" />*/}
          {/*  <span className="text-sm text-gray-500">or</span>*/}
          {/*  <div className="h-px flex-1 bg-gray-300" />*/}
          {/*</div>*/}
          {/*<div className="mt-6 flex items-center justify-center">*/}
          {/*  <button*/}
          {/*    type="button"*/}
          {/*    onClick={() => signInWithRedirect({ provider: "Google" })}*/}
          {/*    className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"*/}
          {/*  >*/}
          {/*    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">*/}
          {/*      <path*/}
          {/*        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"*/}
          {/*        fill="#4285F4"*/}
          {/*      />*/}
          {/*      <path*/}
          {/*        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"*/}
          {/*        fill="#34A853"*/}
          {/*      />*/}
          {/*      <path*/}
          {/*        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"*/}
          {/*        fill="#FBBC05"*/}
          {/*      />*/}
          {/*      <path*/}
          {/*        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"*/}
          {/*        fill="#EA4335"*/}
          {/*      />*/}
          {/*    </svg>*/}
          {/*    Continue with Google*/}
          {/*  </button>*/}
          {/*</div>*/}
          <p className="mt-6 text-center text-sm text-gray-600">
            Did you{" "}
            <a href="/forgot-password" className="text-primary hover:underline">
              forget your password?
            </a>
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            Don&apos;t have an account?{" "}
            <a href="/signup" className="text-primary hover:underline">
              Sign up
            </a>
          </p>
        </div>
      </div>
    </>
  );
}

export default function LoginFormPage(): React.JSX.Element {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
